import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiCorePackage, PapiUser } from '@wix/da-papi-types';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { BiData, BuyCoreButtonClickFromPPBiEvent } from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import ListItemCardRadioButton from '@wix/da-ds/pkg/List/ListItemCard/dsPresets/ListItemCardRadioButton';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { CoreTypes } from '../../types';
import { PackagePickerStoreSlice } from '../../../../redux/packagePicker/types';
import { formatSaleEndDate } from '../../utils';
import PackageSavingsLabel from './_partials/PackageSavingsLabel';
import PackageSublabel from './_partials/PackageSublabel';
import PackageLabel from './_partials/PackageLabel';

import s from './BillingCyclePopup.scss';

export interface Props {
  coreType: CoreTypes;
  className?: string;
  withCloseButton?: boolean;
  isOpen: boolean;
  onRequestClose?(): void;
  selectedPackage?: number[]; // sub product ids for coreType
  filteredPackages?: PapiCorePackage[]; // The packages for the selected core type
  giftUser?: PapiUser;
  sale?: PackagePickerStoreSlice['sale'];
  hasPrepaidSubscription?: boolean;
  alreadyHasSubscription?: boolean;
}

const BillingCyclePopup: React.FC<Props> = ({
  isOpen,
  coreType,
  giftUser,
  sale,
  onRequestClose,
  filteredPackages: packages = [],
  hasPrepaidSubscription,
  alreadyHasSubscription,
}) => {
  const { t } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const [selectedCycle, setSelectedCycle] = useState('year');

  const monthlyPackage = packages?.find(
    p => p.interval === 'month'
  ) as PapiCorePackage;
  const yearlyPackage = packages?.find(
    p => p.interval === 'year'
  ) as PapiCorePackage;

  useEffect(() => {
    setSelectedCycle(
      yearlyPackage?.discount || !monthlyPackage?.discount ? 'year' : 'month'
    );
  }, [monthlyPackage, yearlyPackage]);

  if (!monthlyPackage || !yearlyPackage) {
    return null;
  }

  const pickedPackage = packages.find(p => p.interval === selectedCycle);
  const isGift = !!giftUser;

  // We only show discounts during a sale (not to confuse discount w/ bulk savings).
  // Discount is displayed for one of the packages only, always giving preference to yearly.
  const shouldPackageShowDiscount = (pkg: PapiCorePackage) => {
    return Boolean(
      sale?.cpShowDiscount &&
        pkg.discount &&
        (pkg.interval === 'year' ||
          !packages.find(p => p.interval === 'year')?.discount)
    );
  };
  const anyPackageHasDiscount = packages.reduce(
    (acc, pkg) => (shouldPackageShowDiscount(pkg) ? true : acc),
    false
  );

  const checkoutUrl = Url.checkoutLink(
    pickedPackage?.subproductId || 0,
    giftUser?.userId
  );
  const gotoCheckout = () => window.location.assign(checkoutUrl);

  return (
    <DSModal
      alwaysLight
      isOpen={isOpen}
      heading={t('packagePicker.billingCycle.new.heading')}
      subheading={t(`packagePicker.billingCycle.new.subHeading.${coreType}`)}
      onRequestClose={onRequestClose}
      contentClassName={s['content']}
    >
      <div className={s['selection-area']}>
        <ListItemCardRadioButton
          withControl
          withThickSelectedBorder
          className={s['radio-button']}
          label={
            <PackageLabel
              corePackage={yearlyPackage}
              shouldPackageShowDiscount={shouldPackageShowDiscount(
                yearlyPackage
              )}
            />
          }
          subLabel={
            <PackageSublabel
              corePackage={yearlyPackage}
              shouldPackageShowDiscount={shouldPackageShowDiscount(
                yearlyPackage
              )}
            />
          }
          name="billingCycle"
          value="yearly"
          checked={selectedCycle === 'year'}
          onChange={() => setSelectedCycle('year')}
          hasAccessibleText
          suffix={
            <PackageSavingsLabel
              corePackage={yearlyPackage}
              anyPackageHasDiscount={anyPackageHasDiscount}
              shouldPackageShowDiscount={shouldPackageShowDiscount(
                yearlyPackage
              )}
            />
          }
        />
        <ListItemCardRadioButton
          withControl
          withThickSelectedBorder
          className={s['radio-button']}
          label={
            <PackageLabel
              corePackage={monthlyPackage}
              shouldPackageShowDiscount={shouldPackageShowDiscount(
                monthlyPackage
              )}
            />
          }
          subLabel={
            <PackageSublabel
              corePackage={monthlyPackage}
              shouldPackageShowDiscount={shouldPackageShowDiscount(
                monthlyPackage
              )}
            />
          }
          name="billingCycle"
          value="monthly"
          checked={selectedCycle === 'month'}
          onChange={() => setSelectedCycle('month')}
          hasAccessibleText
          suffix={
            <PackageSavingsLabel
              corePackage={monthlyPackage}
              anyPackageHasDiscount={anyPackageHasDiscount}
              shouldPackageShowDiscount={shouldPackageShowDiscount(
                monthlyPackage
              )}
            />
          }
        />
      </div>
      <div className={s['custom-footer-area']}>
        <BrandedButton
          variant="brand-green"
          size="large"
          onClick={restrictToLoggedIn(
            ReferralType.CORE_CYCLE_PICKER,
            gotoCheckout,
            checkoutUrl
          )}
          className={s['cta']}
          biData={
            pickedPackage
              ? BiData<BuyCoreButtonClickFromPPBiEvent>({
                  evid: 500,
                  give_core_or_become_core: alreadyHasSubscription
                    ? 'upgrade_core'
                    : isGift
                    ? 'give_core'
                    : 'become_core',
                  membershiplength: pickedPackage.interval === 'month' ? 1 : 12,
                  membershiptypeid: pickedPackage.subproductId,
                  referral_info: ReferralType.CORE_BILLING_PAYMENTS,
                })
              : undefined
          }
        >
          {t('packagePicker.billingCycle.new.cta.goToCheckout')}
        </BrandedButton>
        <div className={s['disclaimer']}>
          {sale &&
            anyPackageHasDiscount &&
            t('packagePicker.billingCycle.saleDisclaimer', {
              endDate: formatSaleEndDate(sale.saleEndDate!),
            })}
          {t('packagePicker.billingCycle.new.finalPriceDisclaimer')}
          {alreadyHasSubscription &&
            hasPrepaidSubscription &&
            t('packagePicker.billingCycle.new.prorationDisclaimer')}
        </div>
      </div>
    </DSModal>
  );
};
BillingCyclePopup.displayName = 'BillingCyclePopup';

export default BillingCyclePopup;
